import type { SerializeFrom } from '@remix-run/server-runtime'
import { formatRelative } from 'date-fns'
import {
  BoxIcon,
  CircleDollarSignIcon,
  CircleSlash2Icon,
  EyeIcon,
  HandCoinsIcon,
  InfoIcon,
  ShoppingBagIcon,
} from 'lucide-react'
import { motion } from 'motion/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { getShopAnalyticByShopId } from '~/models/shop_analytic.server'
import { Bars } from '~/ui'
import { centsToEurosFormattedPrice } from '~/utils/number'

const CARD_MOTION_VARIANTS = {
  hidden: { opacity: 0, scale: 0.75 },
  visible: { opacity: 1, scale: 1 },
}

type ShopAnalyticsProps = {
  shopAnalytics?: SerializeFrom<
    Awaited<ReturnType<typeof getShopAnalyticByShopId>>
  >
}

export const ShopAnalytics = ({ shopAnalytics }: ShopAnalyticsProps) => {
  const { t } = useTranslation()

  const shopAnalyticsMetaData = useMemo(
    () => ({
      totalVisits: {
        value: shopAnalytics?.totalVisits || 0.0,
        label: t('page.analytics.store_visits'),
        icon: EyeIcon,
      },
      totalProducts: {
        value: shopAnalytics?.totalProducts || 0.0,
        label: t('page.analytics.products_live'),
        icon: BoxIcon,
      },
      totalOrders: {
        value: shopAnalytics?.totalOrders || 0.0,
        label: t('page.analytics.orders_placed'),
        icon: ShoppingBagIcon,
      },
      totalRevenue: {
        value: shopAnalytics?.totalRevenue
          ? centsToEurosFormattedPrice(shopAnalytics.totalRevenue)
          : 0,
        label: t('page.analytics.revenue'),
        icon: HandCoinsIcon,
      },
      totalSellerNetAmount: {
        value: shopAnalytics?.totalSellerNetAmount
          ? centsToEurosFormattedPrice(shopAnalytics.totalSellerNetAmount)
          : 0,
        label: t('page.analytics.potential_rev'),
        icon: CircleDollarSignIcon,
      },
      averagePaidAmount: {
        value: shopAnalytics?.averagePaidAmount
          ? centsToEurosFormattedPrice(shopAnalytics?.averagePaidAmount)
          : 0,
        label: t('page.analytics.avg_order_value'),
        icon: CircleSlash2Icon,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shopAnalytics]
  )

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={CARD_MOTION_VARIANTS}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      drag
      dragMomentum={false}
      className="fixed right-5 bottom-4 z-50 cursor-move"
    >
      <div className="bg-foreground text-background pointer-events-auto mx-auto w-max rounded-xl shadow-lg">
        <header className="border-background/12 flex items-center justify-between gap-4 border-b px-4 py-2 text-xs">
          <div className="flex items-center gap-1">
            <Bars />
            <p className="max-sm:hidden">Shop analytics</p>
          </div>
          {shopAnalytics?.updatedAt && (
            <div className="text-2xs flex items-center gap-1">
              <span>{`Updated ${formatRelative(shopAnalytics?.updatedAt, Date.now())}`}</span>
              <InfoIcon />
            </div>
          )}
        </header>

        <div className="grid w-full grid-cols-2 gap-4 p-4 pt-2 @sm:grid-cols-3">
          {Object.entries(shopAnalyticsMetaData).map(
            ([keys, { value, label, icon: IconComponent }]) => (
              <div key={keys}>
                <div className="flex flex-col">
                  <p className="text-background text-lg font-medium tabular-nums">
                    {value}
                  </p>
                  <div className="text-background/64 flex items-center gap-1 text-xs font-medium">
                    <IconComponent />
                    <p>{label}</p>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </motion.div>
  )
}
